import React from "react";
import { connect, ConnectedProps } from "react-redux";
import {
  Button,
  Card,
  Container,
  Grid,
  Icon,
  Image,
  Input,
  Loader,
  Message,
  Segment,
  Step
} from "semantic-ui-react";
import { externalLinks } from "../../../../config/config";
import { recoveryCodeFilename } from "../../../../config/constants";
import { AppDispatch, RootState } from "../../../../reducers";
import {
  confirmUserHasSavedRecoveryCode,
  getSetup2faQrImage,
  setup2fa
} from "../../../../store/twoFactorAuthentication";
import { doesNotExist } from "../../../../utils/utils";

const mapStateToProps = ({
  twoFactorAuthentication: {
    isSetup2faQrImageLoading,
    setup2faQrImage,
    isSetup2faLoading,
    setup2faSuccess,
    setup2faFailure,
    setup2faFailureMessage,
    areRecoveryCodesLoading,
    getRecoveryCodesSuccess,
    recoveryCodes,
    isConfirmUserHasSavedRecoveryCodeLoading,
    confirmUserHasSavedRecoveryCodeSuccess,
    confirmUserHasSavedRecoveryCodeFailure,
    confirmUserHasSavedRecoveryCodeFailureMessage
  }
}: RootState) => ({
  setup2faQrImage,
  isSetup2faQrImageLoading,
  isSetup2faLoading,
  setup2faSuccess,
  setup2faFailure,
  setup2faFailureMessage,
  areRecoveryCodesLoading,
  getRecoveryCodesSuccess,
  recoveryCodes,
  isConfirmUserHasSavedRecoveryCodeLoading,
  confirmUserHasSavedRecoveryCodeSuccess,
  confirmUserHasSavedRecoveryCodeFailure,
  confirmUserHasSavedRecoveryCodeFailureMessage
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getSetup2faQrImage: () => dispatch(getSetup2faQrImage()),
  setup2fa: (code: string) => dispatch(setup2fa(code)),
  confirmUserHasSavedRecoveryCode: () => dispatch(confirmUserHasSavedRecoveryCode())
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type TwoFactorAuthenticationSetupProps = {
  onClose: (...args: any[]) => void;
  onSuccess: (...args: any[]) => void;
} & PropsFromRedux;
type TwoFactorAuthenticationSetupState = {
  step: number;
  codeInputValue: string;
};

class TwoFactorAuthenticationSetup extends React.Component<
  TwoFactorAuthenticationSetupProps,
  TwoFactorAuthenticationSetupState
> {
  constructor(props: TwoFactorAuthenticationSetupProps) {
    super(props);
    this.state = { step: 0, codeInputValue: "" };
  }

  componentDidUpdate() {
    if (this.state.step === 1 && this.props.setup2faSuccess) {
      this.setState({ step: this.state.step + 1 });
    }
    if (this.state.step === 2 && this.props.confirmUserHasSavedRecoveryCodeSuccess) {
      this.setState({ step: this.state.step + 1 });
    }
    if (this.state.step === 3) {
      this.props.onSuccess();
    }
  }

  updateCodeInputValue = (evt: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      codeInputValue: evt.target.value
    });
  };

  onNextClick = () => {
    const { step, codeInputValue } = this.state;
    switch (step) {
      case 0:
        this.props.getSetup2faQrImage();
        this.setState({ step: step + 1 });
        break;
      case 1:
        this.props.setup2fa(codeInputValue);
        break;
      case 2:
        this.props.confirmUserHasSavedRecoveryCode();
        break;
      default:
        break;
    }
  };

  onDownloadRecoveryCodes = () => {
    if (doesNotExist(this.props.recoveryCodes)) {
      return;
    }
    const element = document.createElement("a");
    const file = new Blob([this.props.recoveryCodes], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = recoveryCodeFilename;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  renderSetupMethod = () => {
    return (
      <Card.Group>
        <Card>
          <Card.Content>
            <Card.Header>
              <Icon circular inverted color="blue" name="users" />
              <span> Setup using an app</span>
            </Card.Header>
            <Card.Description>
              Use an application on your phone to get two-factor authentication codes when prompted.
              We recommend using{" "}
              <a href={externalLinks.googleAuthenticator} target="_blank" rel="noopener noreferrer">
                Google Authenticator
              </a>
            </Card.Description>
          </Card.Content>
        </Card>
      </Card.Group>
    );
  };

  renderFailureMessage = (message: string | null) => {
    return (
      <Container>
        <Message negative style={{ marginTop: "14px" }}>
          <p>{message}</p>
        </Message>
      </Container>
    );
  };

  renderAuthenticationVerification = () => {
    return (
      <Grid columns={1}>
        <Grid.Row>
          <Grid.Column>
            Scan the image below with the two-factor authentication app on your phone.
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            {this.props.isSetup2faQrImageLoading ? (
              <Loader active inline />
            ) : (
              <Image
                size="small"
                rounded
                bordered
                src={this.props.setup2faQrImage}
                alt="qrcode"
                as="img"
              />
            )}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <p>
              <strong>Enter the code from the application</strong> <br />
              After scanning the QR code image, the app will display a code that you can enter
              below.
            </p>
            <Input
              placeholder="6-digit code"
              maxLength="6"
              value={this.state.codeInputValue}
              onChange={evt => this.updateCodeInputValue(evt)}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  renderRecoveryCodesDownload = () => {
    return (
      <Grid columns={1}>
        <Grid.Row>
          <Grid.Column>Save your recovery codes.</Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            {this.props.areRecoveryCodesLoading ? (
              <Loader active inline />
            ) : (
              <>
                <Segment placeholder className="text-mono">
                  {this.props.recoveryCodes}
                </Segment>
                <Button onClick={this.onDownloadRecoveryCodes}>Download</Button>
              </>
            )}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <p>
              <strong>Why is saving your recovery codes important?</strong> <br />
              If you lose access to your phone, you can authenticate to UrbanSim using your recovery
              codes. We recommend saving them with a secure password manager.
            </p>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  renderAttachedView = () => {
    switch (this.state.step) {
      case 0:
        return this.renderSetupMethod();
      case 1:
        return this.renderAuthenticationVerification();
      case 2:
        return this.renderRecoveryCodesDownload();
      default:
        return null;
    }
  };

  render() {
    const { step, codeInputValue } = this.state;
    const {
      setup2faFailure,
      setup2faFailureMessage,
      isSetup2faLoading,
      isConfirmUserHasSavedRecoveryCodeLoading,
      confirmUserHasSavedRecoveryCodeFailure,
      confirmUserHasSavedRecoveryCodeFailureMessage
    } = this.props;
    return (
      <>
        <Step.Group attached="top" ordered>
          <Step active={step === 0} completed={step > 0}>
            <Step.Content>
              <Step.Title>Two-factor authentication</Step.Title>
              <Step.Description>Extra layer of authentication security</Step.Description>
            </Step.Content>
          </Step>
          <Step active={step === 1} completed={step > 1}>
            <Step.Content>
              <Step.Title>Authentication verification</Step.Title>
              <Step.Description>Scan image with your phone</Step.Description>
            </Step.Content>
          </Step>
          <Step active={step === 2} completed={step > 2}>
            <Step.Content>
              <Step.Title>Recovery codes</Step.Title>{" "}
              <Step.Description>Save your recovery codes</Step.Description>
            </Step.Content>
          </Step>
        </Step.Group>
        <Segment attached>
          <Container>{this.renderAttachedView()}</Container>
          {setup2faFailure ? this.renderFailureMessage(setup2faFailureMessage) : null}
          {confirmUserHasSavedRecoveryCodeFailure
            ? this.renderFailureMessage(confirmUserHasSavedRecoveryCodeFailureMessage)
            : null}
          <Container>
            <Button.Group style={{ marginTop: "14px" }}>
              <Button
                positive
                onClick={this.onNextClick}
                disabled={step === 1 && (doesNotExist(codeInputValue) || codeInputValue.length < 6)}
                loading={isSetup2faLoading || isConfirmUserHasSavedRecoveryCodeLoading}
              >
                {step === 2 ? "I have saved my recovery codes" : "Continue"}
              </Button>
            </Button.Group>
          </Container>
        </Segment>
      </>
    );
  }
}

export default connector(TwoFactorAuthenticationSetup);
